<!-- eslint-disable vue/no-unused-refs -->

<template>
  <div class="inspirational-carousel text-center critical-component-clear">
    <p
      v-if="liquid.title"
      class="inspirational-carousel__title text-mobile-headlines-h3 text-desktop-headlines-h3"
      v-text="liquid.title"
    />

    <splide ref="splide" :has-track="false" :options="carouselOptions">
      <splide-track>
        <splide-slide
          v-for="(product, index) of content"
          :key="product.id || index"
        >
          <product-card
            :product="product || false"
            :is-variant="product.isVariant || false"
            :variant="product.selectedOrFirstAvailableVariant || false"
            location="Section Inspirational Carousel"
            minimal
          />

          <btn
            v-if="liquid.ctaText"
            class="inspirational-carousel__cta"
            modifiers="ghost center no-hover"
            size="medium-large"
            :url="
              product.isVariant
                ? product.selectedOrFirstAvailableVariant.url
                : product.url
            "
            :label="liquid.ctaText"
          />
        </splide-slide>
      </splide-track>

      <div class="inspirational-carousel__arrows splide__arrows">
        <button class="splide__arrow splide__arrow--prev" type="button">
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.previous')"
          />

          <icon-chevron-left />
        </button>

        <button class="splide__arrow splide__arrow--next" type="button">
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.next')"
          />

          <icon-chevron-right />
        </button>
      </div>
    </splide>
  </div>
</template>

<script>
/**
 * Vue: Inspirational carousel (inspirational-carousel)
 * -----------------------------------------------------------------------------
 * Inspirational carousel.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {String} liquid.title - Section title.
 * @param {String} liquid.ctaText - CTA text.
 * @param {Array} products - Array of product,variant IDs and subtitles.
 */

import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide';
import ProductCard from '~global/product-card/product-card.vue';
import Btn from '~global/btn/btn.vue';

import { formatProduct } from '~/format/format-product';
import { transformId } from '~/helpers/graphql';
import nodesByIdProductCardQuery from '~graphql/nodes-by-id-product-card.gql';
import { values as breakpoints } from '~/config/breakpoints';

import IconChevronLeft from '~icons/directional-navigation/chevron/left.svg';
import IconChevronRight from '~icons/directional-navigation/chevron/right.svg';

export default {
  name: 'InspirationalCarousel',

  components: {
    Btn,
    Splide,
    SplideSlide,
    SplideTrack,
    ProductCard,
    IconChevronLeft,
    IconChevronRight,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
        title: '',
        ctaText: '',
      }),
    },
    products: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      content: Array.from({ length: this.products.length }, () => false),
      carouselOptions: {
        arrows: true,
        mediaQuery: 'min',
        pagination: false,
        perMove: 1,
        perPage: 1,
        padding: {
          left: 'var(--spacing-m)',
          right: 'var(--spacing-m)',
        },
        breakpoints: {
          [breakpoints.l]: {
            destroy: true,
          },
        },
      },
    };
  },

  mounted() {
    this.fetchProducts();
  },

  methods: {
    async fetchProducts() {
      if (!this.products.length) {
        return;
      }

      const ids = this.products.map((product) => {
        if (!product.variantId) {
          return transformId(product.id, 'Product');
        }

        return transformId(product.variantId, 'ProductVariant');
      });

      const response = await cnvs.Query({
        query: nodesByIdProductCardQuery,
        variables: {
          country: this.$variable('store.country'),
          language: this.$variable('store.language'),
          ids,
        },
      });

      this.content = response.nodes.filter(Boolean).map((node, index) => {
        const subtitle = this.products[index].subtitle;

        if (node.product) {
          return {
            ...formatProduct(node, { variantId: node.id }),
            subtitle: subtitle ? subtitle : node?.product.subtitle.value,
            isVariant: true,
          };
        }

        return {
          ...formatProduct(node),
          subtitle: subtitle ? subtitle : node?.subtitle.value,
        };
      });
    },
  },
};
</script>

<style lang="scss">
@import './inspirational-carousel';
</style>
